import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExportButtons } from '@/components/ExportButtons';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const COLS = ['date', 'supplier', 'supplier_vat_number', 'invoice_number', 'amount', 'vat_amount', 'branch', 'posted_journal', 'entry_status'];

function VatEntryDialog({ row, onClose, onSaved }) {
  const [f, setF] = useState({ vat_amount: '', vat_inclusive: true, invoice_number: row?.invoice_number || '', invoice_date: row?.date || '', supplier_vat_number: row?.supplier_vat_number || '', verified_tax_invoice: false });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/data-integrity/purchase-vat-entry', { ...f, expense_id: row.id, vat_amount: parseFloat(f.vat_amount) || 0 });
      toast.success(data.message); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="purchase-vat-dialog">
        <DialogHeader><DialogTitle>Enter VAT from tax invoice — {row?.supplier} · {fmt(row?.amount)}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>VAT amount</Label><Input type="number" step="0.01" value={f.vat_amount} onChange={(e) => setF({ ...f, vat_amount: e.target.value })} data-testid="pvat-amount" /></div>
          <div><Label>Supplier VAT number</Label><Input value={f.supplier_vat_number} onChange={(e) => setF({ ...f, supplier_vat_number: e.target.value })} data-testid="pvat-vat-number" /></div>
          <div><Label>Invoice number</Label><Input value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} data-testid="pvat-invoice-number" /></div>
          <div><Label>Invoice date</Label><Input type="date" value={f.invoice_date} onChange={(e) => setF({ ...f, invoice_date: e.target.value })} data-testid="pvat-invoice-date" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.vat_inclusive} onCheckedChange={(v) => setF({ ...f, vat_inclusive: !!v })} data-testid="pvat-inclusive" />Purchase amount already includes VAT</label>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.verified_tax_invoice} onCheckedChange={(v) => setF({ ...f, verified_tax_invoice: !!v })} data-testid="pvat-verified" />I checked the original tax invoice (supplier name, VAT number, VAT shown)</label>
        <p className="text-xs text-muted-foreground">Posted purchases are not changed — this is saved as a proposal for approval.</p>
        <Button onClick={save} disabled={saving} data-testid="pvat-save-btn">{saving ? 'Saving…' : 'Save proposal'}</Button>
      </DialogContent>
    </Dialog>
  );
}

export function PurchaseVatTab({ params, page, setPage }) {
  const [d, setD] = useState(null);
  const [row, setRow] = useState(null);
  const load = useCallback(() => { api.get('/data-integrity/purchases-missing-vat', { params: { ...params, page } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [params, page]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-3" data-testid="purchase-vat-tab">
      <div className="text-sm" data-testid="pvat-summary">{d.total} purchases without VAT · {fmt(d.amount)} · {d.with_valid_supplier_vat} with a valid supplier VAT number</div>
      <p className="text-xs text-muted-foreground">{d.note}</p>
      <div className="flex justify-between items-center">
        <div className="flex gap-2 text-xs items-center">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pvat-prev">Prev</Button><span>Page {page}</span>
          <Button size="sm" variant="outline" disabled={d.rows.length < 100} onClick={() => setPage(page + 1)} data-testid="pvat-next">Next</Button>
        </div>
        <ExportButtons data={d.rows} columns={COLS} filename="purchases_missing_vat" testid="pvat-export" />
      </div>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-xs">
          <thead className="bg-muted/50"><tr>{COLS.map((c) => <th key={c} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{c.replace(/_/g, ' ')}</th>)}<th /></tr></thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.id} className="border-t">
                {COLS.map((c) => <td key={c} className="px-2 py-1 whitespace-nowrap">{typeof r[c] === 'number' ? fmt(r[c]) : (r[c] || '—')}{c === 'supplier_vat_number' && r[c] && !r.vat_number_valid ? ' (invalid)' : ''}</td>)}
                <td className="px-2 py-1">{!r.entry_status && <Button size="sm" variant="outline" onClick={() => setRow(r)} data-testid={`pvat-enter-${r.id.slice(0, 8)}`}>Enter VAT</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {row && <VatEntryDialog row={row} onClose={() => setRow(null)} onSaved={() => { setRow(null); load(); }} />}
    </div>
  );
}
